import React, { useEffect, useState } from 'react'
import moment from 'moment'
import {
  Box,
  Button,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  CircularProgress,
  Chip,
  Avatar,
  makeStyles
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { Add, Delete, Edit, MailOutline, PersonAddOutlined, Close } from '@material-ui/icons'
import { Formik, Form } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'utils/notify'
import * as Yup from 'yup'

import {
  fetchAllUsers,
  createUser,
  deleteUserByUserId,
  updateUserPasswordByUserId,
  resendActivationEmail
} from 'store/Admins'
import ConfirmModal from 'components/ConfirmationModal'
import DuplicateAssignmentDialog from 'components/DuplicateAssignmentDialog'
import CustomTextField from 'components/Form/CustomTextField'
import PasswordField from 'components/PasswordField'
import ProgressButton from 'components/ProgressButton'
import { Roles } from 'constants/index'
import { getConferenceRole, getConferenceStatus, isAssignedToConference } from 'utils/conference'

// No year here — this table renders inside ModularConferenceForm's narrower
// left column, where the standalone listing's full "MMM D, YYYY h:mm A" wraps.
const formatInviteDate = (ms) => (ms ? moment(ms).format('MMM D, h:mm A') : '—')

const isInviteExpired = (admin) =>
  Boolean(admin.inviteExpiresAt) && Date.now() > admin.inviteExpiresAt

const createValidationSchema = Yup.object({
  fullName: Yup.string().required('Full Name is required'),
  email: Yup.string().email('Invalid email address').required('Email is required'),
  role: Yup.string()
    .oneOf(['admin', 'owner'], 'Role must be admin or owner')
    .required('Role is required'),
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .test(
      'password-strength',
      'Password must contain at least one letter and one number',
      (value) => {
        return /^(?=.*[A-Za-z])(?=.*\d)/.test(value)
      }
    )
    .required('Password is required')
})

const editValidationSchema = Yup.object({
  fullName: Yup.string().required('Full Name is required'),
  role: Yup.string()
    .oneOf(['admin', 'owner'], 'Role must be admin or owner')
    .required('Role is required'),
  newPassword: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .test(
      'password-strength',
      'Password must contain at least one letter and one number',
      (value) => {
        if (!value) return true // Optional field
        return /^(?=.*[A-Za-z])(?=.*\d)/.test(value)
      }
    )
})

const ConferenceAdminsForm = ({ conference, user }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [userToDelete, setUserToDelete] = useState(null)
  const [isCreating, setIsCreating] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [userToEdit, setUserToEdit] = useState(null)
  const [isUpdating, setIsUpdating] = useState(false)
  const [pendingUser, setPendingUser] = useState(null)
  const [duplicateConferences, setDuplicateConferences] = useState([])
  const [pendingPasswordUpdate, setPendingPasswordUpdate] = useState(null)
  const [resendTarget, setResendTarget] = useState(null)
  const [isResending, setIsResending] = useState(false)

  const { admins, inProgress } = useSelector((state) => state.admins)

  // Guards against showing another conference's users while this conference's fetch is
  // still in flight — the store holds a single shared list.
  const conferenceAdmins = admins.filter((admin) => isAssignedToConference(admin, conference?.id))

  useEffect(() => {
    if (conference?.id) {
      dispatch(fetchAllUsers({ conferenceId: conference.id, currentUserRole: user.role }))
    }
  }, [dispatch, conference?.id])

  const isOrganizer = user?.role === Roles.ORGANIZER && conference?.organizerId === user?.id

  // Only show for super-admin, owner, and organizer (for their own conferences)
  if (
    !user ||
    (![Roles.SUPER_ADMIN, Roles.ACCOUNT_OWNER].includes(user.role) && !isOrganizer) ||
    !conference?.id
  ) {
    return null
  }

  const isSuperAdmin = user.role === Roles.SUPER_ADMIN || isOrganizer

  const handleAddAdmin = () => {
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
  }

  const handleDeleteAdmin = (admin) => {
    setUserToDelete(admin)
    setIsDeleteModalOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (userToDelete) {
      try {
        setIsDeleting(true)
        // The thunk takes { uid, confId } — passing a bare uid sent both through as
        // undefined and the delete silently no-oped (rejectWithValue never throws).
        const result = await dispatch(
          deleteUserByUserId({ uid: userToDelete.uid, confId: conference.id })
        )
        if (deleteUserByUserId.rejected.match(result)) {
          toast.error('Could not delete admin.')
        } else {
          // Refresh the list
          await dispatch(fetchAllUsers({ conferenceId: conference.id, currentUserRole: user.role }))
        }
      } catch (error) {
        toast.error('Could not delete admin.')
      } finally {
        setIsDeleting(false)
      }
    }
    setIsDeleteModalOpen(false)
    setUserToDelete(null)
  }

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false)
    setUserToDelete(null)
  }

  const handleEditAdmin = (admin) => {
    setUserToEdit(admin)
    setIsEditModalOpen(true)
  }

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false)
    setUserToEdit(null)
  }

  const submitEditAdmin = async (values, { setSubmitting, resetForm }) => {
    setIsUpdating(true)
    const payload = {
      uid: userToEdit.uid,
      confId: conference.id,
      fullName: values.fullName.trim()
    }

    // Add new password only if provided
    if (values.newPassword) {
      payload.newPassword = values.newPassword
    }

    // Add role if user is super admin
    if (values.role) {
      payload.role = values.role
    }

    try {
      await dispatch(updateUserPasswordByUserId(payload))
      // Refresh the admin list
      dispatch(fetchAllUsers({ conferenceId: conference.id, currentUserRole: user.role }))
      resetForm()
      setIsEditModalOpen(false)
      setUserToEdit(null)
      // toast.success('User updated successfully')
    } catch (error) {
      const errorMsg =
        error.response?.data?.error?.message || error.response?.data?.msg || 'Failed to update user'
      toast.error(errorMsg)
    }

    setIsUpdating(false)
    setSubmitting(false)
  }

  const handleEditSubmit = async (values, formikHelpers) => {
    if (values.newPassword) {
      // Password changes need explicit confirmation — defer to the dialog below.
      formikHelpers.setSubmitting(false)
      setPendingPasswordUpdate({ values, formikHelpers })
      return
    }
    await submitEditAdmin(values, formikHelpers)
  }

  const handleConfirmPasswordUpdate = async () => {
    if (!pendingPasswordUpdate) return
    const { values, formikHelpers } = pendingPasswordUpdate
    setPendingPasswordUpdate(null)
    await submitEditAdmin(values, formikHelpers)
  }

  const handleResendActivation = async () => {
    if (!resendTarget) return
    setIsResending(true)
    await dispatch(resendActivationEmail({ uid: resendTarget.uid, callerUid: user?.id }))
    await dispatch(fetchAllUsers({ conferenceId: conference.id, currentUserRole: user.role }))
    setIsResending(false)
    setResendTarget(null)
  }

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setIsCreating(true)
    const payload = {
      fullName: values.fullName.trim(),
      email: values.email,
      password: values.password,
      confId: conference.id,
      role: values.role, // Add role to payload
      callerUid: user?.id
    }

    try {
      const result = await createUser(payload)
      // The email already belongs to a conference user — nothing was created, so keep the modal
      // open instead of reporting success.
      if (result?.alreadyAssigned) {
        const isInCurrentConf = result.conferences?.some((c) => c.id === conference.id)
        if (isInCurrentConf) {
          toast.warning(
            'This user is already assigned to this conference. Please use a different email address.'
          )
        } else {
          setPendingUser({ values, resetForm })
          setDuplicateConferences(result.conferences ?? [])
        }
        setIsCreating(false)
        setSubmitting(false)
        return
      }
      // toast.success('Conference admin created successfully')
      // Refresh the admin list for real-time updates
      dispatch(fetchAllUsers({ conferenceId: conference.id, currentUserRole: user.role }))
      resetForm()
      setIsModalOpen(false)
    } catch (error) {
      const errorMsg =
        error.response?.data?.error?.message ||
        error.response?.data?.msg ||
        'Failed to create conference admin'
      toast.error(errorMsg)
    }

    setIsCreating(false)
    setSubmitting(false)
  }

  const handleConfirmDuplicate = async () => {
    if (!pendingUser) return
    setIsCreating(true)
    try {
      await createUser({
        fullName: pendingUser.values.fullName.trim(),
        email: pendingUser.values.email,
        password: pendingUser.values.password,
        confId: conference.id,
        role: pendingUser.values.role,
        callerUid: user?.id,
        force: true
      })
      dispatch(fetchAllUsers({ conferenceId: conference.id, currentUserRole: user.role }))
      pendingUser.resetForm()
      setIsModalOpen(false)
      setPendingUser(null)
      setDuplicateConferences([])
    } catch (error) {
      const errorMsg =
        error.response?.data?.error?.message ||
        error.response?.data?.msg ||
        'Failed to create conference admin'
      toast.error(errorMsg)
    }
    setIsCreating(false)
  }

  const handleCancelDuplicate = () => {
    setPendingUser(null)
    setDuplicateConferences([])
  }

  const getRoleChipColor = (role) => {
    switch (role) {
      case 'owner':
        return 'primary'
      case 'admin':
        return 'default'
      default:
        return 'default'
    }
  }

  const getAdminRole = (admin) => getConferenceRole(admin, conference.id)

  const getAdminStatus = (admin) => getConferenceStatus(admin, conference.id)

  const getAvailableRoles = () => {
    if (user.role === Roles.SUPER_ADMIN || isOrganizer) {
      return [
        { value: 'admin', label: 'Admin' },
        { value: 'owner', label: 'Owner' }
      ]
    }
    // Owner can only add admins
    if (user.role === Roles.ACCOUNT_OWNER) {
      return [{ value: 'admin', label: 'Admin' }]
    }
    return []
  }

  return (
    <Paper className={classes.section}>
      <Box className={classes.sectionHeader}>
        <Typography variant="h6" color="secondary" style={{ fontWeight: 'bold' }}>
          Conference {isSuperAdmin ? 'Users' : 'Admins'}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={handleAddAdmin}
          size="small">
          Add {isSuperAdmin ? 'User' : 'Admin'}
        </Button>
      </Box>

      {inProgress ? (
        <Box className={classes.loadingContainer}>
          <CircularProgress size={24} />
          <Typography variant="body2" style={{ marginLeft: 8 }}>
            Loading {isSuperAdmin ? 'users...' : 'admins...'}
          </Typography>
        </Box>
      ) : conferenceAdmins.length === 0 ? (
        <Typography variant="body2" className={classes.emptyState}>
          No conference {isSuperAdmin ? 'user' : 'admin'} found. Add the first{' '}
          {isSuperAdmin ? 'user' : 'admin'} to get started.
        </Typography>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Invited</TableCell>
                <TableCell>Expires</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {conferenceAdmins.map((admin) => {
                const pendingActivation = admin.mustChangePassword
                const expired = isInviteExpired(admin)
                return (
                  <TableRow key={admin.uid}>
                    <TableCell>{admin.fullName}</TableCell>
                    <TableCell>{admin.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={getAdminRole(admin)}
                        color={getRoleChipColor(getAdminRole(admin))}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          pendingActivation
                            ? 'Pending Activation'
                            : getAdminStatus(admin) === 'in-active'
                            ? 'Inactive'
                            : 'Active'
                        }
                        size="small"
                        style={{
                          backgroundColor: pendingActivation
                            ? '#f59e0b'
                            : getAdminStatus(admin) === 'in-active'
                            ? '#f44336'
                            : '#4caf50',
                          color: '#fff'
                        }}
                      />
                    </TableCell>
                    <TableCell>{formatInviteDate(admin.inviteSentAt)}</TableCell>
                    <TableCell>{formatInviteDate(admin.inviteExpiresAt)}</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                        {pendingActivation && (
                          <Tooltip
                            title={
                              expired
                                ? 'Resend Activation Email'
                                : `Invitation still valid until ${formatInviteDate(
                                    admin.inviteExpiresAt
                                  )} — resend becomes available once it expires`
                            }>
                            <span>
                              <IconButton
                                size="small"
                                disabled={!expired}
                                onClick={() => setResendTarget(admin)}
                                color="primary">
                                <MailOutline />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleEditAdmin(admin)}
                            color="primary">
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteAdmin(admin)}
                            color="danger">
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add Admin Modal */}
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{ className: classes.dialogPaper }}>
        <Box className={classes.dialogHeader}>
          <Box className={classes.dialogHeaderLeft}>
            <Box className={classes.dialogIconWrap}>
              <PersonAddOutlined className={classes.dialogIcon} />
            </Box>
            <Typography className={classes.dialogTitleText}>
              Add Conference {isSuperAdmin ? 'User' : 'Admin'}
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleCloseModal} className={classes.dialogCloseBtn}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
        <Formik
          initialValues={{
            fullName: '',
            email: '',
            role: getAvailableRoles()[0]?.value || 'admin',
            password: ''
          }}
          validationSchema={createValidationSchema}
          onSubmit={handleSubmit}>
          {({ values, setFieldValue, isSubmitting, touched, errors, handleBlur }) => (
            <Form>
              <DialogContent className={classes.dialogContent}>
                <Alert severity="info" className={classes.guideNote}>
                  This {isSuperAdmin ? 'user' : 'admin'} will be invited by email with the temporary
                  password below and a link to set their own password. They must complete that setup
                  before they can log in — the link expires after 48 hours.
                </Alert>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <CustomTextField
                      name="fullName"
                      label="Full Name"
                      fullWidth
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <CustomTextField
                      name="email"
                      label="Email"
                      type="email"
                      fullWidth
                      variant="outlined"
                      autoComplete="off"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Role</InputLabel>
                      <Select
                        value={values.role}
                        onChange={(e) => setFieldValue('role', e.target.value)}
                        label="Role">
                        {getAvailableRoles().map((role) => (
                          <MenuItem key={role.value} value={role.value}>
                            {role.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <PasswordField
                      name="password"
                      label="Temporary Password"
                      autoComplete="new-password"
                      formik={{
                        values,
                        handleChange: (e) => setFieldValue(e.target.name, e.target.value),
                        handleBlur,
                        touched,
                        errors
                      }}
                    />
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions className={classes.dialogActionsBox}>
                <Button
                  onClick={handleCloseModal}
                  disabled={isCreating || isSubmitting}
                  color="default">
                  Cancel
                </Button>
                <ProgressButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  inProgress={isCreating || isSubmitting}
                  disabled={isCreating || isSubmitting}
                  label={`Create ${isSuperAdmin ? 'User' : 'Admin'}`}
                />
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>

      {/* Edit Admin Modal */}
      <Dialog
        open={isEditModalOpen}
        onClose={handleCloseEditModal}
        maxWidth="md"
        fullWidth
        PaperProps={{ className: classes.dialogPaper }}>
        <Box className={classes.dialogHeader}>
          <Box className={classes.dialogHeaderLeft}>
            <Box className={classes.dialogIconWrap}>
              <Edit className={classes.dialogIcon} />
            </Box>
            <Typography className={classes.dialogTitleText}>
              Edit Conference {isSuperAdmin ? 'User' : 'Admin'}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={handleCloseEditModal}
            className={classes.dialogCloseBtn}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
        <Formik
          initialValues={{
            fullName: userToEdit?.fullName || '',
            role: userToEdit ? getAdminRole(userToEdit) : 'admin',
            newPassword: ''
          }}
          validationSchema={editValidationSchema}
          onSubmit={handleEditSubmit}>
          {({ values, setFieldValue, isSubmitting, touched, errors, handleBlur }) => (
            <Form>
              <DialogContent className={classes.dialogContent}>
                <Box className={classes.identityCard}>
                  <Avatar className={classes.identityAvatar}>
                    {userToEdit?.fullName?.charAt(0)?.toUpperCase() ?? 'U'}
                  </Avatar>
                  <Box className={classes.identityText}>
                    <Typography className={classes.identityName}>{userToEdit?.fullName}</Typography>
                    <Typography className={classes.identityEmail}>{userToEdit?.email}</Typography>
                  </Box>
                  <Chip
                    label={userToEdit ? getAdminRole(userToEdit) : ''}
                    color={getRoleChipColor(userToEdit ? getAdminRole(userToEdit) : '')}
                    size="small"
                    className={classes.identityRoleChip}
                  />
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <CustomTextField
                      name="fullName"
                      label="Full Name"
                      fullWidth
                      variant="outlined"
                      onBlur={handleBlur}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Role</InputLabel>
                      <Select
                        value={values.role}
                        onChange={(e) => setFieldValue('role', e.target.value)}
                        onBlur={handleBlur}
                        label="Role">
                        {getAvailableRoles().map((role) => (
                          <MenuItem key={role.value} value={role.value}>
                            {role.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <PasswordField
                      name="newPassword"
                      label="New Password (Optional)"
                      autoComplete="new-password"
                      disabled={userToEdit?.mustChangePassword}
                      formik={{
                        values,
                        handleChange: (e) => setFieldValue(e.target.name, e.target.value),
                        handleBlur,
                        touched,
                        errors
                      }}
                    />
                    {userToEdit?.mustChangePassword && (
                      <Alert severity="warning" className={classes.inlineWarning}>
                        This user hasn&apos;t completed account setup yet — password can&apos;t be
                        changed until then. Use &quot;Resend Activation Email&quot; from the list
                        instead.
                      </Alert>
                    )}
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions className={classes.dialogActionsBox}>
                <Button
                  onClick={handleCloseEditModal}
                  disabled={isUpdating || isSubmitting}
                  color="default">
                  Cancel
                </Button>
                <ProgressButton
                  type="submit"
                  size="small"
                  variant="contained"
                  color="primary"
                  inProgress={isUpdating || isSubmitting}
                  disabled={isUpdating || isSubmitting}
                  label={`Update ${isSuperAdmin ? 'User' : 'Admin'}`}
                />
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>

      {/* Already-assigned Confirmation Dialog */}
      <DuplicateAssignmentDialog
        conferences={duplicateConferences}
        inProgress={isCreating}
        onCancel={handleCancelDuplicate}
        onConfirm={handleConfirmDuplicate}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        dialogTitle={`Delete Conference ${isSuperAdmin ? 'User' : 'Admin'}`}
        dialogText={`Are you sure you want to delete ${userToDelete?.fullName}? This action cannot be undone.`}
        open={isDeleteModalOpen}
        inProgress={isDeleting}
        closeModal={handleCloseDeleteModal}
        handleConfirm={handleConfirmDelete}
      />

      {/* Password Change Confirmation Modal */}
      <ConfirmModal
        dialogTitle="Change Password"
        dialogText={`Are you sure you want to change the password for ${
          userToEdit?.fullName || 'this user'
        }? They will need to use the new password the next time they log in.`}
        open={Boolean(pendingPasswordUpdate)}
        inProgress={isUpdating}
        closeModal={() => setPendingPasswordUpdate(null)}
        handleConfirm={handleConfirmPasswordUpdate}
      />

      {/* Resend Activation Email Confirmation Modal */}
      <ConfirmModal
        dialogTitle="Resend Activation Email"
        dialogText={`This will invalidate the current activation link and send ${
          resendTarget?.fullName || 'this user'
        } a brand-new temporary password and link. Continue?`}
        open={Boolean(resendTarget)}
        inProgress={isResending}
        closeModal={() => setResendTarget(null)}
        handleConfirm={handleResendActivation}
      />
    </Paper>
  )
}

const useStyles = makeStyles(() => ({
  section: {
    marginBottom: '24px',
    padding: '18px 24px',
    borderRadius: '8px',
    boxShadow: 'rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 2px 6px 2px',
    backgroundColor: '#ffffff'
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px'
  },
  sectionTitle: {
    fontWeight: 600,
    borderBottom: '1px solid #eee',
    paddingBottom: '10px',
    flex: 1,
    marginRight: '16px'
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px'
  },
  emptyState: {
    textAlign: 'center',
    color: '#999',
    fontStyle: 'italic',
    padding: '24px'
  },
  guideNote: {
    marginBottom: '1rem'
  },
  dialogPaper: {
    borderRadius: '16px'
  },
  dialogHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 24px 16px',
    borderBottom: '1px solid #eef0f3'
  },
  dialogHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    minWidth: 0
  },
  dialogIconWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    borderRadius: '10px',
    backgroundColor: 'rgba(21, 180, 219, 0.12)',
    flexShrink: 0
  },
  dialogIcon: {
    color: '#15b4db',
    fontSize: '22px'
  },
  dialogTitleText: {
    fontWeight: 700,
    fontSize: '18px',
    color: '#1a2b3c'
  },
  dialogCloseBtn: {
    color: '#94a3b8'
  },
  dialogContent: {
    padding: '24px'
  },
  dialogActionsBox: {
    padding: '16px 24px',
    borderTop: '1px solid #eef0f3',
    gap: '8px'
  },
  identityCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '14px 16px',
    borderRadius: '10px',
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    marginBottom: '1.5rem'
  },
  identityAvatar: {
    width: 44,
    height: 44,
    fontWeight: 700,
    fontSize: '1.1rem',
    backgroundColor: '#0ea5e9',
    color: '#fff',
    flexShrink: 0
  },
  identityText: {
    flex: 1,
    minWidth: 0
  },
  identityName: {
    fontWeight: 700,
    fontSize: '0.95rem',
    color: '#0f172a'
  },
  identityEmail: {
    fontSize: '0.8rem',
    color: '#64748b'
  },
  identityRoleChip: {
    flexShrink: 0
  },
  inlineWarning: {
    marginTop: '0.75rem'
  }
}))

export default ConferenceAdminsForm
